import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, MessageCircle } from "lucide-react";
import { SITE, waHref } from "@/data/site";

const links = [
  { to: "/", label: "Anasayfa" },
  { to: "/hakkimizda", label: "Hakkımızda" },
  { to: "/hizmetlerimiz", label: "Hizmetler" },
  { to: "/urunlerimiz", label: "Ürünler" },
  { to: "/blog", label: "Blog" },
  { to: "/hizli-randevu", label: "Hızlı Randevu" },
  { to: "/iletisim", label: "İletişim" },
];

function MobileSheet({ open, onClose }) {
  if (typeof document === "undefined") return null;
  const overlay = (
    <div
      data-testid="mobile-sheet"
      aria-hidden={!open}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 100,
        backgroundColor: "#FFFFFF",
        opacity: open ? 1 : 0,
        visibility: open ? "visible" : "hidden",
        pointerEvents: open ? "auto" : "none",
        transition: "opacity 260ms ease, visibility 260ms ease",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          height: 64,
          borderBottom: "1px solid #E5EAF0",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 20px",
          backgroundColor: "#FFFFFF",
          flexShrink: 0,
        }}
      >
        <Link to="/" onClick={onClose} className="font-display text-2xl font-semibold text-[#0E2B4F]">
          Medi<span className="text-[#10B981]">Este</span>
        </Link>
        <button
          onClick={onClose}
          aria-label="Kapat"
          className="inline-flex items-center justify-center h-10 w-10 rounded-full border border-[#D1D9E4] text-[#0E2B4F]"
        >
          <X size={18} />
        </button>
      </div>

      <nav
        style={{
          flex: 1,
          overflowY: "auto",
          padding: "24px 24px 120px",
          backgroundColor: "#FFFFFF",
        }}
      >
        <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
          {links.map((l) => (
            <li key={l.to}>
              <NavLink
                to={l.to}
                end={l.to === "/"}
                onClick={onClose}
                data-testid={`mobile-nav-${l.to.replace("/", "") || "home"}`}
className={({ isActive }) =>
  `block py-3 border-b border-[#E5EAF0] font-display text-base font-medium transition-colors ${
    isActive ? "text-[#10B981]" : "text-[#0B1B33]"
  }`
}
              >
                {l.label}
              </NavLink>
            </li>
          ))}
        </ul>

        <Link
          to="/hizli-randevu"
          onClick={onClose}
          className="mt-8 inline-flex items-center justify-center gap-2 h-14 rounded-full bg-gradient-to-br from-[#A8C520] to-[#3F7A2E] text-white text-sm tracking-[0.15em] uppercase font-semibold w-full"
        >
          <MessageCircle size={18} /> Hızlı Randevu
        </Link>
        <a
          href={waHref()}
          target="_blank"
          rel="noreferrer"
          onClick={onClose}
          className="mt-3 inline-flex items-center justify-center gap-2 h-14 rounded-full bg-[#10B981] text-white text-sm tracking-[0.15em] uppercase font-semibold w-full"
        >
          <MessageCircle size={18} /> WhatsApp ile Yaz
        </a>
        <a
          href={`tel:${SITE.phoneRaw}`}
          onClick={onClose}
          className="mt-3 inline-flex items-center justify-center gap-2 h-14 rounded-full border-2 border-[#0E2B4F] text-[#0E2B4F] text-sm tracking-[0.15em] uppercase font-semibold w-full"
        >
          <Phone size={16} /> {SITE.phone}
        </a>

        <div className="mt-10 pt-6 border-t border-[#E5EAF0] text-center">
          <p className="text-xs text-[#7C8CA0] leading-relaxed">
            Bağlıca Bulvarı, Mert Caddesi<br />No:4/2 Etimesgut / Ankara
          </p>
        </div>
      </nav>
    </div>
  );
  return createPortal(overlay, document.body);
}

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${scrolled ? "nav-scrolled" : "bg-white/90 backdrop-blur border-b border-transparent"}`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 md:h-20 flex items-center justify-between">
          <Link to="/" data-testid="site-logo" className="flex items-center gap-2 group">
            <span className="font-display text-2xl md:text-3xl tracking-tight text-[#0E2B4F] group-hover:text-[#10B981] transition-colors font-semibold">
              Medi<span className="text-[#10B981]">Este</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-${l.to.replace("/", "") || "home"}`}
                className={({ isActive }) =>
                  `text-[13px] tracking-wide font-medium transition-colors duration-200 ${
                    isActive ? "text-[#10B981]" : "text-[#0B1B33]/80 hover:text-[#10B981]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <a
              href={`tel:${SITE.phoneRaw}`}
              data-testid="tap-to-call"
              aria-label="Ara"
              className="lg:hidden inline-flex items-center justify-center h-10 w-10 rounded-full bg-[#10B981] text-white hover:bg-[#059669] transition-colors"
            >
              <Phone size={16} />
            </a>
            <Link
              to="/hizli-randevu"
              data-testid="header-cta-randevu"
              className="hidden md:inline-flex items-center gap-2 h-10 px-5 rounded-full bg-gradient-to-br from-[#A8C520] to-[#3F7A2E] text-white text-xs tracking-[0.15em] uppercase font-semibold hover:brightness-110 hover:shadow-[0_10px_24px_rgba(63,122,46,0.35)] transition-all"
            >
              <MessageCircle size={14} /> Randevu Al
            </Link>
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen(true)}
              className="lg:hidden inline-flex items-center justify-center h-10 w-10 rounded-full border border-[#D1D9E4] text-[#0E2B4F]"
              aria-label="Menü"
            >
              <Menu size={18} />
            </button>
          </div>
        </div>
      </header>

      <MobileSheet open={open} onClose={() => setOpen(false)} />
    </>
  );
}
